<template>
  
  <div>
     <div class="card-history-container">
      <div >
         <table class="card-history-table">
            <thead>
               <tr>
                 <th v-for="header in headers"
                     :key="header.key"
                     :class="{'active-header' : activeHeader === header.key}"
                     @click="setActiveHeader(header.key)"
                 >
                 <div class="d-flex justify-content-between">
                  <span>{{ header.label }}</span>
                  <div>
                     <i v-if="activeHeader === header.key && sortOrder === 'asc'" class="fa fa-sort-asc"></i>
                     <i v-else-if="activeHeader === header.key && sortOrder === 'desc'" class="fa fa-sort-desc"></i>
                     <i v-else class="fa fa-sort muted"></i>
                  </div>
                 </div>
                  
                 </th>
                 
               </tr>
            </thead>
            <tbody>
               <tr v-if="!sortedHistory || sortedHistory.length === 0">
                  <td :colspan="headers.length" style="text-align: center; padding: 40px;">
                     No history found
                  </td>
               </tr>
               <tr v-for="(history, index) in sortedHistory" :key="index">
                  <td>{{ formatDateTime(history.created_at) }}</td>
                  <td>{{ history.EAID || 'N/A' }}</td>
                  <td>{{ formatStatus(history.status) }}</td>
                  <td>{{ formatChangedByType(history.changed_by_type) }}</td>
                  <td>{{ history.driver_name || 'N/A' }}</td>
                  <td>{{ history.changed_by_name || 'N/A' }}</td>
               </tr>
            </tbody>
         </table>
      </div>
     </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useCardStore } from '@/store/admin/card';
import { formatDateTime } from '@/utils/formatters';

const cardStore = useCardStore();

const headers = [
   {key: 'date', label: 'Date'},
   {key: 'eaid', label: 'EAID'},
   {key: 'cardStatus', label: 'Card status'},
   {key: 'assignment', label: 'Assignment'},
   {key: 'assignedTo', label: 'Assigned to'},
   {key: 'updatedBy', label: 'Updated by'},
];
const activeHeader = ref(headers[0].key);
const sortOrder = ref('desc');

const setActiveHeader = (header) => {
   if (activeHeader.value === header) {
      // 如果點擊同一個標題，切換排序順序
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
   } else {
      activeHeader.value = header;
      sortOrder.value = 'desc';
   }
}

// 排序後的歷史記錄
const sortedHistory = computed(() => {
   if (!cardStore.cardHistory || cardStore.cardHistory.length === 0) {
      return [];
   }
   
   const history = [...cardStore.cardHistory];
   const order = sortOrder.value === 'asc' ? 1 : -1;
   
   history.sort((a, b) => {
      let aValue, bValue;
      
      switch (activeHeader.value) {
         case 'date':
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
         case 'eaid':
            aValue = (a.EAID || '').toLowerCase();
            bValue = (b.EAID || '').toLowerCase();
            break;
         case 'cardStatus':
            aValue = (a.status || '').toLowerCase();
            bValue = (b.status || '').toLowerCase();
            break;
         case 'assignment':
            aValue = (a.changed_by_type || '').toLowerCase();
            bValue = (b.changed_by_type || '').toLowerCase();
            break;
         case 'assignedTo':
            aValue = (a.driver_name || '').toLowerCase();
            bValue = (b.driver_name || '').toLowerCase();
            break;
         case 'updatedBy':
            aValue = (a.changed_by_name || '').toLowerCase();
            bValue = (b.changed_by_name || '').toLowerCase();
            break;
         default:
            return 0;
      }
      
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
   });
   
   return history;
});

// 載入卡片歷史記錄
const loadCardHistory = async () => {
   const driverId = cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id;
   if (!driverId) return;
   
   try {
      const eaid = cardStore.cardInfo?.EAID ?? cardStore.cardInfo?.eaid;
      await cardStore.fetchCardHistory(driverId, eaid != null && eaid !== '' ? { eaid } : {});
   } catch (error) {
      console.error('Failed to fetch card history:', error);
   }
};

// 監聽 driver / EAID 變化（同 driver 多卡時需依 EAID 重載）
watch(
   () => [cardStore.cardInfo?.driver?.id, cardStore.cardInfo?.EAID ?? cardStore.cardInfo?.eaid],
   async ([newDriverId]) => {
      if (newDriverId) {
         await loadCardHistory();
      }
   },
   { immediate: true }
);

// 組件掛載時載入資料
onMounted(async () => {
   await loadCardHistory();
});

// 使用工具函數 formatDateTime（已從 @/utils/formatters 導入）

// 格式化狀態
const formatStatus = (status) => {
   if (!status) return 'N/A';
   // 將狀態轉換為首字母大寫
   return status.charAt(0).toUpperCase() + status.slice(1);
};

// 格式化變更者類型
const formatChangedByType = (type) => {
   if (!type) return 'N/A';
   return type.charAt(0).toUpperCase() + type.slice(1);
};




</script>

<style scoped>
.card-history-table{
   width: 100%;
   table-layout: fixed;
}
.card-history-table thead th{
   border-right: 1px solid #e4e7ec;
   padding: 10px;
}
.card-history-table thead th:last-child{
   border:none;
}
.card-history-table thead th.active-header{
   border-bottom:2px solid #00C962;
   color:#00C962;
}
.card-history-table tbody td{
   padding-top:30px;
   border-bottom:1px solid #e4e7ec;
   padding-bottom:10px;

}
.card-history-table thead th .muted{
   opacity:0.8;
}

/* 手機版響應式設計 */
@media (max-width: 768px) {
  .card-history-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .card-history-table {
    min-width: 800px;
    table-layout: auto;
  }

  .card-history-table thead th {
    padding: 12px 10px;
    font-size: 13px;
    min-width: 100px;
  }

  .card-history-table thead th span {
    font-size: 12px;
    white-space: nowrap;
    display: inline-block;
  }

  .card-history-table thead th .fa {
    font-size: 12px;
    margin-left: 4px;
  }

  .card-history-table thead th .d-flex {
    gap: 8px;
  }

  .card-history-table tbody td {
    padding: 20px 10px 12px;
    font-size: 13px;
    white-space: nowrap;
    word-break: break-word;
  }

  .card-history-table tbody td:first-child {
    min-width: 120px;
  }

  .card-history-table tbody td:nth-child(2) {
    min-width: 110px;
  }

  .card-history-table tbody td:nth-child(3),
  .card-history-table tbody td:nth-child(4) {
    min-width: 100px;
  }

  .card-history-table tbody td:nth-child(5) {
    min-width: 150px;
  }

  .card-history-table tbody td:nth-child(6) {
    min-width: 100px;
  }
}

/* 小手機版 (小於 576px) */
@media (max-width: 576px) {
  .card-history-table {
    min-width: 700px;
  }

  .card-history-table thead th {
    padding: 10px 8px;
    font-size: 12px;
    min-width: 90px;
  }

  .card-history-table thead th span {
    font-size: 11px;
  }

  .card-history-table thead th .fa {
    font-size: 11px;
  }

  .card-history-table tbody td {
    padding: 18px 8px 10px;
    font-size: 12px;
  }

  .card-history-table tbody td:first-child {
    min-width: 110px;
  }

  .card-history-table tbody td:nth-child(2) {
    min-width: 100px;
  }

  .card-history-table tbody td:nth-child(3),
  .card-history-table tbody td:nth-child(4) {
    min-width: 90px;
  }

  .card-history-table tbody td:nth-child(5) {
    min-width: 140px;
  }

  .card-history-table tbody td:nth-child(6) {
    min-width: 90px;
  }
}
</style>